import { useCallback, useEffect, useMemo, useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  getEmployeeTypeList,
  createEmployeeType,
  updateEmployeeType,
  deleteEmployeeType,
  getEmployeeList,
  updateEmployeeTypeBatch,
} from "@/api/employee"
import type { EmployeeType } from "@/types/employee"
import { toast } from "sonner"
import { Loader2, Plus, Save, X, Edit2, Trash2 } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface EmployeeTypeManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shopNo?: number
  onTypesUpdated?: () => Promise<void> | void
}

type EditingState = {
  typeId: number
  value: string
} | null

export function EmployeeTypeManagerDialog({
  open,
  onOpenChange,
  shopNo,
  onTypesUpdated,
}: EmployeeTypeManagerDialogProps) {
  const [types, setTypes] = useState<EmployeeType[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [editingState, setEditingState] = useState<EditingState>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [newTypeName, setNewTypeName] = useState("")
  const [pendingId, setPendingId] = useState<number | "new" | null>(null)
  const [reassignDialogState, setReassignDialogState] = useState<{
    open: boolean
    sourceType: EmployeeType | null
    employeeCount: number
  }>({ open: false, sourceType: null, employeeCount: 0 })
  const [replacementTypeId, setReplacementTypeId] = useState<number | null>(null)
  const [isReassigning, setIsReassigning] = useState(false)

  const canManage = useMemo(() => typeof shopNo === "number" && shopNo > 0, [shopNo])

  const resetFormStates = useCallback(() => {
    setEditingState(null)
    setIsAdding(false)
    setNewTypeName("")
    setPendingId(null)
    setReassignDialogState({ open: false, sourceType: null, employeeCount: 0 })
    setReplacementTypeId(null)
    setIsReassigning(false)
  }, [])

  const fetchTypes = useCallback(async () => {
    if (!canManage) return
    setIsFetching(true)
    try {
      const response = await getEmployeeTypeList({ shopNo: shopNo! })
      if (response.code === 200 && response.data) {
        const fetched = response.data.types ?? []
        setTypes(fetched.slice().reverse())
      } else {
        toast.error(response.message || "임직원 구분 목록을 불러오지 못했습니다.")
      }
    } catch (error) {
      console.error("임직원 구분 목록 조회 오류:", error)
      toast.error("임직원 구분 목록을 불러오는 중 오류가 발생했습니다.")
    } finally {
      setIsFetching(false)
    }
  }, [canManage, shopNo])

  const availableReplacementTypes = useMemo(() => {
    const excludedId = reassignDialogState.sourceType?.typeId
    return excludedId
      ? types.filter((type) => type.typeId !== excludedId)
      : types
  }, [types, reassignDialogState.sourceType])

  useEffect(() => {
    if (reassignDialogState.open) {
      setReplacementTypeId((prev) => {
        if (prev && availableReplacementTypes.some((type) => type.typeId === prev)) {
          return prev
        }
        return availableReplacementTypes.length > 0 ? availableReplacementTypes[0].typeId : null
      })
    } else {
      setReplacementTypeId(null)
    }
  }, [reassignDialogState.open, availableReplacementTypes])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetFormStates()
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange, resetFormStates]
  )

  const handleStartAdd = useCallback(() => {
    setEditingState(null)
    setIsAdding(true)
    setNewTypeName("")
  }, [])

  const handleCreateType = useCallback(async () => {
    if (!canManage) return
    const trimmed = newTypeName.trim()
    if (!trimmed) {
      toast.error("구분명을 입력해 주세요.")
      return
    }
    setPendingId("new")
    try {
      const response = await createEmployeeType({ typeName: trimmed, shopNo: shopNo! })
      if (response.code === 200 || response.code === 201) {
        toast.success("구분이 추가되었습니다.")
        await fetchTypes()
        resetFormStates()
        await onTypesUpdated?.()
      } else {
        toast.error(response.message || "구분 추가에 실패했습니다.")
      }
    } catch (error) {
      console.error("구분 추가 오류:", error)
      toast.error("구분을 추가하는 중 오류가 발생했습니다.")
    } finally {
      setPendingId(null)
    }
  }, [canManage, newTypeName, shopNo, fetchTypes, resetFormStates, onTypesUpdated])

  const handleEditInit = useCallback((type: EmployeeType) => {
    setIsAdding(false)
    setNewTypeName("")
    setEditingState({ typeId: type.typeId, value: type.typeName })
  }, [])

  const handleEditCancel = useCallback(() => {
    setEditingState(null)
  }, [])

  const handleUpdateType = useCallback(async () => {
    if (!editingState) return
    const trimmed = editingState.value.trim()
    if (!trimmed) {
      toast.error("구분명을 입력해 주세요.")
      return
    }
    setPendingId(editingState.typeId)
    try {
      const response = await updateEmployeeType(editingState.typeId, { typeName: trimmed })
      if (response.code === 200) {
        toast.success("구분이 수정되었습니다.")
        await fetchTypes()
        setEditingState(null)
        await onTypesUpdated?.()
      } else {
        toast.error(response.message || "구분 수정에 실패했습니다.")
      }
    } catch (error) {
      console.error("구분 수정 오류:", error)
      toast.error("구분을 수정하는 중 오류가 발생했습니다.")
    } finally {
      setPendingId(null)
    }
  }, [editingState, fetchTypes, onTypesUpdated])

  const performDeleteType = useCallback(
    async (type: EmployeeType) => {
      try {
        const response = await deleteEmployeeType(type.typeId)
        if (response.code === 200) {
          toast.success("구분이 삭제되었습니다.")
          await fetchTypes()
          await onTypesUpdated?.()
          return true
        } else {
          toast.error(response.message || "구분 삭제에 실패했습니다.")
          return false
        }
      } catch (error) {
        console.error("구분 삭제 오류:", error)
        toast.error("구분을 삭제하는 중 오류가 발생했습니다.")
        return false
      }
    },
    [fetchTypes, onTypesUpdated]
  )

  const handleDeleteType = useCallback(
    async (type: EmployeeType) => {
      if (!canManage) return
      setPendingId(type.typeId)
      try {
        const response = await getEmployeeList({
          shopNo: shopNo!,
          employeeTypeId: type.typeId,
          page: 1,
          limit: 1,
        })

        if (response.code !== 200 || !response.data) {
          toast.error(response.message || "임직원 정보를 확인할 수 없습니다.")
          return
        }

        const totalEmployees = response.data.total ?? 0

        if (totalEmployees === 0) {
          if (!confirm(`"${type.typeName}" 구분을 삭제하시겠습니까?`)) {
            return
          }
          await performDeleteType(type)
          return
        }

        const confirmationMessage = `"${type.typeName}" 을 삭제하시겠습니까?\n현재 ${totalEmployees.toLocaleString()}명 임직원이 "${type.typeName}" 으로 등록되어 있습니다.\n확인을 클릭하시면 등록된 구분을 선택하여 변경 후 삭제처리할 수 있습니다.`

        if (!confirm(confirmationMessage)) {
          return
        }

        const alternatives = types.filter((item) => item.typeId !== type.typeId)
        if (alternatives.length === 0) {
          toast.error("변경할 수 있는 다른 구분이 없습니다. 새로운 구분을 먼저 추가해 주세요.")
          return
        }

        setReassignDialogState({
          open: true,
          sourceType: type,
          employeeCount: totalEmployees,
        })
      } catch (error) {
        console.error("구분 삭제 사전 검증 오류:", error)
        toast.error("구분 삭제를 준비하는 중 오류가 발생했습니다.")
      } finally {
        setPendingId(null)
      }
    },
    [canManage, performDeleteType, shopNo, types]
  )

  const handleReassignCancel = useCallback(() => {
    if (isReassigning) return
    setReassignDialogState({ open: false, sourceType: null, employeeCount: 0 })
    setReplacementTypeId(null)
  }, [isReassigning])

  const handleReassignConfirm = useCallback(async () => {
    const sourceType = reassignDialogState.sourceType
    if (!sourceType || !canManage) return
    if (!replacementTypeId) {
      toast.error("변경할 구분을 선택해 주세요.")
      return
    }
    if (replacementTypeId === sourceType.typeId) {
      toast.error("다른 구분을 선택해 주세요.")
      return
    }

    setIsReassigning(true)
    try {
      const response = await updateEmployeeTypeBatch({
        typeId: sourceType.typeId,
        changeTypeId: replacementTypeId,
        shopNo: shopNo!,
      })

      if (response.code !== 200) {
        toast.error(response.message || "임직원 구분 변경에 실패했습니다.")
        return
      }

      const deleted = await performDeleteType(sourceType)
      if (deleted) {
        setReassignDialogState({ open: false, sourceType: null, employeeCount: 0 })
        setReplacementTypeId(null)
      }
    } catch (error) {
      console.error("임직원 구분 일괄 변경 오류:", error)
      toast.error("임직원 구분을 변경하는 중 오류가 발생했습니다.")
    } finally {
      setIsReassigning(false)
    }
  }, [canManage, performDeleteType, replacementTypeId, reassignDialogState.sourceType, shopNo])

  useEffect(() => {
    if (open && canManage) {
      fetchTypes()
    }
  }, [open, canManage, fetchTypes])

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>임직원 구분 설정</DialogTitle>
            <DialogDescription>
              임직원 구분을 추가하거나 수정, 삭제할 수 있습니다. 목록 순서는 등록 순서입니다.
            </DialogDescription>
          </DialogHeader>

          {!canManage ? (
            <div className="flex h-32 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
              쇼핑몰 정보를 불러온 뒤 다시 시도해 주세요.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  등록된 구분 {types.length.toLocaleString()}개
                </span>
                <Button variant="outline" size="sm" onClick={handleStartAdd} disabled={isAdding}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  추가
                </Button>
              </div>

              {isAdding && (
                <div className="rounded-lg border bg-muted/30 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <Input
                        placeholder="구분명을 입력해 주세요."
                        value={newTypeName}
                        onChange={(event) => setNewTypeName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault()
                            if (newTypeName.trim() && pendingId !== "new") {
                              handleCreateType()
                            }
                          }
                        }}
                        autoFocus
                        disabled={pendingId === "new"}
                        className="w-full min-w-0"
                      />
                    </div>
                    <div className="flex gap-2 sm:flex-none sm:justify-end">
                      <Button
                        size="sm"
                        onClick={handleCreateType}
                        disabled={pendingId === "new"}
                      >
                        {pendingId === "new" ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        저장
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={resetFormStates}
                        disabled={pendingId === "new"}
                      >
                        <X className="h-4 w-4" />
                        취소
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-lg border">
                <div className="max-h-[360px] overflow-y-auto">
                  <div className="w-full overflow-x-hidden">
                    <div className="divide-y">
                    {isFetching && (
                      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        구분 목록을 불러오는 중입니다...
                      </div>
                    )}

                    {!isFetching && types.length === 0 && (
                      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                        등록된 구분이 없습니다. 새로운 구분을 추가해 주세요.
                      </div>
                    )}

                    {!isFetching &&
                      types.map((type) => {
                        const isEditing = editingState?.typeId === type.typeId
                        const isPending = pendingId === type.typeId
                        return (
                          <div
                            key={type.typeId}
                            className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-5"
                          >
                            <div className="flex flex-1 min-w-0 items-center gap-3 sm:pr-4">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground">
                                {type.typeId}
                              </span>
                              {isEditing ? (
                              <Input
                                value={editingState.value}
                                onChange={(event) =>
                                  setEditingState({
                                    typeId: type.typeId,
                                    value: event.target.value,
                                  })
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault()
                                    if (!isPending && editingState.value.trim()) {
                                      handleUpdateType()
                                    }
                                  }
                                }}
                                disabled={isPending}
                                autoFocus
                                className="w-full min-w-0"
                              />
                              ) : (
                                <span className="text-base font-medium truncate">{type.typeName}</span>
                              )}
                            </div>

                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2 sm:w-auto sm:flex-none">
                              {isEditing ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={handleUpdateType}
                                    disabled={isPending}
                                  >
                                    {isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Save className="h-4 w-4" />
                                    )}
                                    저장
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleEditCancel}
                                    disabled={isPending}
                                  >
                                    <X className="h-4 w-4" />
                                    취소
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditInit(type)}
                                    disabled={pendingId !== null}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                    수정
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => handleDeleteType(type)}
                                    disabled={pendingId !== null}
                                  >
                                    {isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                    삭제
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={reassignDialogState.open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            handleReassignCancel()
          }
        }}
      >
        <DialogContent className="sm:max-w-md" onEscapeKeyDown={(event) => {
          if (isReassigning) {
            event.preventDefault()
          }
        }} onPointerDownOutside={(event) => {
          if (isReassigning) {
            event.preventDefault()
          }
        }}>
          <DialogHeader>
            <DialogTitle>임직원 구분 선택</DialogTitle>
            <DialogDescription>
              변경하려는 임직원 구분을 선택해 주세요.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Label>구분</Label>
            <Select
              value={replacementTypeId ? replacementTypeId.toString() : undefined}
              onValueChange={(value) => setReplacementTypeId(Number(value))}
              disabled={availableReplacementTypes.length === 0 || isReassigning}
            >
              <SelectTrigger  className="w-full">
                <SelectValue placeholder="변경할 구분 선택" />
              </SelectTrigger>
              <SelectContent>
                {availableReplacementTypes.length === 0 ? (
                  <SelectItem value="no-types" disabled>
                    선택 가능한 구분이 없습니다.
                  </SelectItem>
                ) : (
                  availableReplacementTypes.map((type) => (
                    <SelectItem key={type.typeId} value={type.typeId.toString()}>
                      {type.typeName}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleReassignCancel} disabled={isReassigning}>
              취소
            </Button>
            <Button
              onClick={handleReassignConfirm}
              disabled={isReassigning || !replacementTypeId}
            >
              {isReassigning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              수정
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
